import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, getListItems } from '../../services/api';
import { getImageUrl } from '../../utils/imageUrl';
import NeedHelpWidget from './NeedHelpWidget';

/**
 * Shared CMS-driven sidebar used on internal detail pages.
 */
function SidebarWidgets({
  categoryType = 'tour',
  showRecentPosts = true,
  showSearch = true,
  excludePostId = null,
}) {
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [recentPosts, setRecentPosts] = useState([]);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        const [catRes, tagRes, settingsRes, blogRes] = await Promise.all([
          api.getCategories(categoryType),
          api.getTags(),
          api.getSettings(),
          showRecentPosts ? api.getBlogPosts({ per_page: excludePostId != null ? 6 : 3 }) : Promise.resolve({ data: [] }),
        ]);
        if (!active) return;
        setCategories(getListItems(catRes));
        setTags(getListItems(tagRes));
        setSettings(settingsRes?.data || {});
        setRecentPosts(
          getListItems(blogRes)
            .filter((post) => excludePostId == null || String(post.id) !== String(excludePostId))
            .slice(0, 3)
        );
      } catch {
        // Keep empty widgets if API fails
      }
    }

    load();
    return () => {
      active = false;
    };
  }, [categoryType, showRecentPosts, excludePostId]);

  const categoryLink = categoryType === 'blog' ? '/blog' : categoryType === 'destination' ? '/destination' : '/tour';

  return (
    <aside className="sidebar-area">
      {showSearch && (
        <div className="widget widget_search">
          <form className="search-form" onSubmit={(e) => e.preventDefault()}>
            <input type="text" placeholder="Search" />
            <button type="submit">
              <i className="far fa-search" />
            </button>
          </form>
        </div>
      )}

      {categories.length > 0 && (
        <div className="widget widget_categories">
          <h3 className="widget_title">Categories</h3>
          <ul>
            {categories.map((cat) => (
              <li key={cat.id}>
                <Link to={categoryLink}>
                  <img src={getImageUrl(cat.image) || '/assets/img/theme-img/map.svg'} alt="" />
                  {cat.name}
                </Link>
                <span>({cat.item_count ?? 0})</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {showRecentPosts && recentPosts.length > 0 && (
        <div className="widget">
          <h3 className="widget_title">Recent Posts</h3>
          <div className="recent-post-wrap">
            {recentPosts.map((post) => (
              <div className="recent-post" key={post.id}>
                <div className="media-img">
                  <Link to={`/blog/${post.id}`}>
                    <img src={getImageUrl(post.image, 'blog')} alt={post.title} />
                  </Link>
                </div>
                <div className="media-body">
                  <h4 className="post-title">
                    <Link className="text-inherit" to={`/blog/${post.id}`}>
                      {post.title}
                    </Link>
                  </h4>
                  <div className="recent-post-meta">
                    <Link to="/blog">
                      <i className="fa-regular fa-calendar" />
                      {post.published_at
                        ? new Date(post.published_at).toLocaleDateString()
                        : ''}
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tags.length > 0 && (
        <div className="widget widget_tag_cloud">
          <h3 className="widget_title">Popular Tags</h3>
          <div className="tagcloud">
            {tags.map((tag) => (
              <Link to="/blog" key={tag.id}>
                {tag.name}
              </Link>
            ))}
          </div>
        </div>
      )}

      <NeedHelpWidget settings={settings} />
    </aside>
  );
}

export default SidebarWidgets;
